<template>
  <tr :class="{ 'closed-row': !editable }">
    <!-- Período (Semestre) -->
    <td v-if="visible_columns.period">
      <select
        v-if="editable"
        class="custom-select custom-select-sm"
        :value="egress_profile_matter.plan_level"
        :aria-label="$t('studyPlan.academicLoad.period')"
        @change="(event) => patchPlanLevel(event.target.value)"
      >
        <option v-for="level in levels" :key="level" :value="level">
          {{ level }}
        </option>
      </select>
      <template v-else>{{ egress_profile_matter.plan_level }}</template>
    </td>
    <!-- Código interno: matter.code de la Asignatura Base (no el código del perfil) -->
    <td v-if="visible_columns.internal_code">
      <input
        v-if="editable && matter"
        type="text"
        placeholder="-"
        class="form-control form-control-sm code-input"
        :value="matter.code"
        :aria-label="$t('studyPlan.academicLoad.internalCode')"
        @change="onCodeChange"
      />
      <template v-else>{{ (matter && matter.code) || "-" }}</template>
    </td>
    <!-- Clave: texto libre de la Asignatura Base (academic_load_key), no es el código -->
    <td v-if="visible_columns.code">
      <input
        v-if="editable && matter"
        type="text"
        placeholder="-"
        class="form-control form-control-sm code-input"
        :value="matter.academic_load_key"
        :aria-label="$t('studyPlan.academicLoad.code')"
        @change="(event) => patchKey(event.target.value)"
      />
      <template v-else>{{ (matter && matter.academic_load_key) || "-" }}</template>
    </td>
    <!-- Nombre: siempre el de la Asignatura Base -->
    <td class="text-left">
      <input
        v-if="editable && matter"
        type="text"
        class="form-control form-control-sm name-input"
        :value="matter.name"
        :aria-label="$t('studyPlan.academicLoad.name')"
        @change="(event) => patchName(event.target.value)"
      />
      <template v-else-if="matter">{{ matter.name }}</template>
    </td>
    <!-- Pre-requisito -->
    <td v-if="visible_columns.prerequisites">
      <div class="d-flex align-items-center justify-content-center">
        <span class="prerequisites-label">{{
          prerequisites_label || "-"
        }}</span>
        <button
          v-if="editable"
          type="button"
          class="btn btn-link btn-sm p-0 ml-1 noprint"
          :title="$t('studyPlan.academicLoad.editPrerequisites')"
          @click="emit('edit-prerequisites', egress_profile_matter)"
        >
          &#9998;
        </button>
      </div>
    </td>
    <!-- Cr. PUCV y Cr. SCT = round(c × 1,5) -->
    <td v-if="visible_columns.pucv_credits">
      <input
        v-if="editable"
        type="number"
        min="0"
        class="form-control form-control-sm number-input"
        :value="credits"
        :aria-label="$t('studyPlan.academicLoad.pucvCredits')"
        @change="(event) => patchCredits(event.target.value)"
      />
      <template v-else>{{ credits }}</template>
    </td>
    <td v-if="visible_columns.sct_credits">{{ sctCredits }}</td>
    <!-- Detalle Horas (semanales) -->
    <td
      v-for="time_allocation in visible_columns.hours"
      :key="`hours-${time_allocation.id}`"
    >
      <input
        v-if="editable"
        type="number"
        min="0"
        step="0.5"
        placeholder="-"
        class="form-control form-control-sm number-input"
        :value="weeklyHoursOf(time_allocation) || ''"
        :title="hoursHint"
        :aria-label="time_allocation.name"
        @change="
          (event) => patchWeeklyHours(time_allocation, event.target.value)
        "
      />
      <template v-else>{{ weeklyHoursOf(time_allocation) || "-" }}</template>
    </td>
    <!-- Totales Horas -->
    <template v-if="visible_columns.presential">
      <td class="total-cell">{{ totals.presential_weekly }}</td>
      <td class="total-cell">{{ totals.presential_semester }}</td>
    </template>
    <template v-if="visible_columns.autonomous">
      <td
        class="total-cell"
        :class="{ 'text-danger font-weight-bold': autonomousIsNegative }"
        :title="autonomousIsNegative ? autonomousNegativeText : ''"
      >
        {{ totals.autonomous_weekly }}
      </td>
      <td
        class="total-cell"
        :class="{ 'text-danger font-weight-bold': autonomousIsNegative }"
        :title="autonomousIsNegative ? autonomousNegativeText : ''"
      >
        {{ totals.autonomous_semester }}
      </td>
    </template>
    <template v-if="visible_columns.dedication">
      <td class="total-cell">{{ totals.dedication_weekly }}</td>
      <td class="total-cell">{{ totals.dedication_semester }}</td>
    </template>
    <!-- Cupo Máximo (N° de estudiantes por División de tiempo) -->
    <td
      v-for="time_allocation in visible_columns.quota"
      :key="`quota-${time_allocation.id}`"
    >
      <input
        v-if="editable"
        type="number"
        min="0"
        placeholder="-"
        class="form-control form-control-sm number-input"
        :value="allocationValue(time_allocation, 'students_per_hour') || ''"
        :aria-label="time_allocation.name"
        @change="(event) => patchQuota(time_allocation, event.target.value)"
      />
      <template v-else>{{
        allocationValue(time_allocation, "students_per_hour") || "-"
      }}</template>
    </td>
    <!-- Tipo de sala -->
    <td
      v-for="time_allocation in visible_columns.classroom_type"
      :key="`classroom-${time_allocation.id}`"
    >
      <select
        v-if="editable"
        class="custom-select custom-select-sm select-input"
        :value="allocationValue(time_allocation, 'classroom_type') || ''"
        :aria-label="time_allocation.name"
        @change="
          (event) =>
            saveAllocation(time_allocation, {
              classroom_type: event.target.value
                ? Number(event.target.value)
                : null,
            })
        "
      >
        <option value="">-</option>
        <option
          v-for="classroom_type in classroom_types"
          :key="classroom_type.id"
          :value="classroom_type.id"
        >
          {{ classroom_type.name }}
        </option>
      </select>
      <template v-else>{{ classroomTypeName(time_allocation) }}</template>
    </td>
    <!-- Periodicidad -->
    <td
      v-for="time_allocation in visible_columns.periodicity"
      :key="`periodicity-${time_allocation.id}`"
    >
      <select
        v-if="editable"
        class="custom-select custom-select-sm select-input"
        :value="allocationValue(time_allocation, 'periodicity') || ''"
        :aria-label="time_allocation.name"
        @change="
          (event) =>
            saveAllocation(time_allocation, {
              periodicity: event.target.value,
            })
        "
      >
        <option
          v-for="option in periodicityOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}
        </option>
      </select>
      <template v-else>{{
        periodicityLabelOf(allocationValue(time_allocation, "periodicity"))
      }}</template>
    </td>
    <!-- Justificación de cupo y Observación -->
    <td v-for="field in noteColumns" :key="field" class="text-left">
      <textarea
        v-if="editable"
        rows="2"
        class="form-control form-control-sm text-input"
        :value="source ? source[field] : ''"
        @change="(event) => patchSource({ [field]: event.target.value })"
      ></textarea>
      <span v-else class="pre-line">{{
        (source && source[field]) || "-"
      }}</span>
    </td>
  </tr>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Fila de la Carga Académica: una asignatura del perfil con las columnas del Anexo 4 PUCV.
// La lógica vive en useAcademicLoadRow.js.
import { computed } from "vue";
import useAcademicLoadRow from "./useAcademicLoadRow";

const props = defineProps({
  egress_profile_matter: {
    type: Object,
    required: true,
  },
  // Asignatura Base; puede llegar después que la del perfil
  matter: {
    type: Object,
    default: null,
  },
  time_allocations: {
    type: Array,
    required: true,
  },
  // Columnas a mostrar, ver visibleColumns en useAcademicLoadColumns
  visible_columns: {
    type: Object,
    required: true,
  },
  // { [time_allocation_id]: división de la asignatura }, ver allocationsOfMatter
  allocations: {
    type: Object,
    required: true,
  },
  classroom_types: {
    type: Array,
    required: true,
  },
  module_minutes: {
    type: Number,
    required: true,
  },
  semester_amount: {
    type: Number,
    default: 1,
  },
  prerequisites_label: {
    type: String,
    default: "",
  },
  allows_crud: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["edit-prerequisites"]);

const {
  source,
  editable,
  credits,
  sctCredits,
  levels,
  totals,
  autonomousIsNegative,
  autonomousNegativeText,
  hoursHint,
  periodicityOptions,
  allocationValue,
  weeklyHoursOf,
  classroomTypeName,
  periodicityLabelOf,
  patchSource,
  patchPlanLevel,
  patchCode,
  patchKey,
  patchName,
  patchCredits,
  patchWeeklyHours,
  patchQuota,
  saveAllocation,
} = useAcademicLoadRow(props);

// El input no se re-renderiza si matter.code no cambia, así que se deja a mano en el valor
// guardado: cubre el código rechazado (repetido), el normalizado (mayúsculas) y el error
function onCodeChange(event) {
  const input = event.target;
  const restore = () => {
    input.value = (props.matter && props.matter.code) || "";
  };
  patchCode(input.value).then(restore, restore);
}

// Columnas visibles de las celdas que se arman con v-for
const noteColumns = computed(() =>
  ["quota_justification", "academic_load_observation"].filter(
    (field) => props.visible_columns[field]
  )
);
</script>

<style scoped>
td {
  vertical-align: middle;
  text-align: center;
  background-color: var(--kl-surface);
  color: var(--kl-text-body);
  font-size: var(--secondary-font-size);
  padding: 0.2rem;
}
.closed-row td {
  background-color: var(--kl-surface-muted, #f5f5f5);
}
.number-input {
  min-width: 3.5rem;
  text-align: center;
}
.code-input {
  min-width: 6rem;
}
.name-input {
  min-width: 14rem;
}
.select-input {
  min-width: 7rem;
}
.text-input {
  min-width: 14rem;
}
.total-cell {
  font-weight: var(--kl-fw-bold);
}
.prerequisites-label {
  white-space: nowrap;
}
.pre-line {
  white-space: pre-line;
}
</style>
